import { EditDocxFileModal } from "@/components/features/matter/file-repo/EditDocxFileSlideover";
import { EditPdfFileModal } from "@/components/features/matter/file-repo/EditPdfFileSlideover";
import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import { EditDocxTemplateModal } from "@/components/features/settings/defaults/matter-templates/EditDocxTemplateModal";
import { EditDocxFirmFileModal } from "@/components/features/settings/firm/EditDocxFirmFileModal";
import { EditPdfFirmFileModal } from "@/components/features/settings/firm/EditPdfFirmFileModal";
import { Button, Typography } from "@/components/ui";
import type { FirmFile, MatterFile, MatterTemplate } from "@/lib/types";
import { getFirmFileDisplayName } from "@/lib/utils/firmFile";
import { getFileIcon, getFileNameParts } from "@/lib/utils/matterFile";
import { Pencil } from "lucide-react";
import { useCallback, useEffect, useState, type ComponentProps } from "react";

export type SignatureRequestPreviewDocument =
  | { type: "matter-file"; document: MatterFile }
  | { type: "matter-template"; document: MatterTemplate }
  | { type: "firm-file"; document: FirmFile };

interface SignatureRequestDocumentPreviewProps {
  matterId: string;
  selectedDocument: SignatureRequestPreviewDocument | null;
  canEditDocumentFiles: boolean;
  editButtonLabel?: string;
  renderPageOverlay?: ComponentProps<typeof FileViewer>["renderPageOverlay"];
  templateFieldScope?: "all" | "signers-only";
  emptyTitle: string;
  emptyDescription: string;
  openEditorRequestKey?: number;
  onMatterFileSaved?: (matterFileId: string) => void | Promise<void>;
  onFirmFileSaved?: (id: string) => void | Promise<void>;
  onTemplateSaved?: (
    id: string,
    hasSignerVariables: boolean,
  ) => void | Promise<void>;
}

export const SignatureRequestDocumentPreview = ({
  matterId,
  selectedDocument,
  canEditDocumentFiles,
  editButtonLabel = "Edit",
  renderPageOverlay,
  templateFieldScope = "all",
  emptyTitle,
  emptyDescription,
  openEditorRequestKey,
  onMatterFileSaved,
  onFirmFileSaved,
  onTemplateSaved,
}: SignatureRequestDocumentPreviewProps) => {
  const [isDocxEditorOpen, setIsDocxEditorOpen] = useState(false);
  const [isPdfEditorOpen, setIsPdfEditorOpen] = useState(false);
  const selectedFile =
    selectedDocument?.type === "matter-file" ? selectedDocument.document : null;
  const selectedTemplate =
    selectedDocument?.type === "matter-template"
      ? selectedDocument.document
      : null;
  const selectedFirmFile =
    selectedDocument?.type === "firm-file" ? selectedDocument.document : null;
  const selectedDocumentFileName = selectedFile
    ? selectedFile.name
    : selectedTemplate
      ? getFileNameParts(selectedTemplate.name).fileExtension
        ? selectedTemplate.name
        : selectedTemplate.presignedUrlKey
      : selectedFirmFile
        ? getFirmFileDisplayName(selectedFirmFile)
        : "";

  const isSelectedDocx =
    Boolean(selectedDocument) &&
    getFileNameParts(selectedDocumentFileName).fileExtension === "docx";
  const isSelectedPdf =
    Boolean(selectedDocument) &&
    getFileNameParts(selectedDocumentFileName).fileExtension === "pdf";

  const previewFile = selectedDocumentFileName
    ? selectedFile
      ? {
          fileName: selectedDocumentFileName,
          presignedURLKey: selectedFile.presignedUrlKey ?? "",
          fileId: selectedFile.id,
        }
      : selectedTemplate?.presignedUrlKey
        ? {
            fileName: selectedDocumentFileName,
            presignedURLKey: selectedTemplate.presignedUrlKey,
          }
        : selectedFirmFile
          ? {
              fileName: selectedDocumentFileName,
              presignedURLKey: selectedFirmFile.presignedUrlKey ?? "",
              fileId: selectedFirmFile.id,
            }
          : undefined
    : undefined;
  const FileIcon = selectedDocumentFileName
    ? getFileIcon(selectedDocumentFileName)
    : null;
  const canEditSelectedFile =
    canEditDocumentFiles && (isSelectedDocx || isSelectedPdf);
  const handleEditSelectedFile = useCallback(() => {
    if (isSelectedDocx && canEditDocumentFiles) {
      setIsDocxEditorOpen(true);
      return;
    }

    if (isSelectedPdf && canEditDocumentFiles) {
      setIsPdfEditorOpen(true);
    }
  }, [canEditDocumentFiles, isSelectedDocx, isSelectedPdf]);

  useEffect(() => {
    if (!openEditorRequestKey) {
      return;
    }

    handleEditSelectedFile();
  }, [handleEditSelectedFile, openEditorRequestKey]);

  return (
    <>
      <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
        {selectedDocument ? (
          previewFile ? (
            <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
              <div className="border-border/80 flex items-center justify-between gap-3 border-b px-4 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  {FileIcon && (
                    <FileIcon className="h-4 w-4 shrink-0 text-blue-500" />
                  )}
                  <Typography className="truncate text-sm font-medium">
                    {selectedFile?.name ??
                      selectedTemplate?.name ??
                      (selectedFirmFile
                        ? getFirmFileDisplayName(selectedFirmFile)
                        : undefined)}
                  </Typography>
                </div>
                {canEditSelectedFile ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 shrink-0 gap-1.5 px-3"
                    onClick={() => {
                      handleEditSelectedFile();
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    {editButtonLabel}
                  </Button>
                ) : null}
              </div>
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
                <FileViewer
                  file={previewFile}
                  fileType={
                    selectedDocument.type === "matter-template"
                      ? "matter-template"
                      : selectedDocument.type === "firm-file"
                        ? "firm-file"
                        : "matter-file"
                  }
                  showDownload={false}
                  showTitle={false}
                  versionControlsContainer={null}
                  footerClassName="hidden"
                  viewerClassName="bg-card min-h-0 h-full flex-1 items-stretch justify-stretch"
                  wrapperClassName="bg-card h-full min-h-0 overflow-hidden rounded-none shadow-none"
                  compactDocxPreview
                  showDocxModeToggle={isSelectedDocx}
                  canEditDocx={isSelectedDocx && canEditDocumentFiles}
                  onDocxEdit={() => {
                    handleEditSelectedFile();
                  }}
                  showEditOverlay={isSelectedPdf}
                  canEditOverlay={isSelectedPdf && canEditDocumentFiles}
                  onEdit={() => {
                    handleEditSelectedFile();
                  }}
                  hidePreviewOverlayControls
                  renderPageOverlay={renderPageOverlay}
                />
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <Typography weight="semibold">Preview not available.</Typography>
              <Typography className="text-muted-foreground text-sm">
                This file does not have a preview link.
              </Typography>
            </div>
          )
        ) : (
          <div className="flex h-full flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <Typography weight="semibold">{emptyTitle}</Typography>
            <Typography className="text-muted-foreground text-sm">
              {emptyDescription}
            </Typography>
          </div>
        )}
      </div>

      {selectedFile && isSelectedDocx && (
        <EditDocxFileModal
          open={isDocxEditorOpen}
          setOpen={setIsDocxEditorOpen}
          matterId={matterId}
          sourceFile={selectedFile}
          onSaved={() => {
            void onMatterFileSaved?.(selectedFile.id);
          }}
          showSaveAsCopyButton={false}
          templateFieldScope={templateFieldScope}
        />
      )}

      {selectedFile && isSelectedPdf && (
        <EditPdfFileModal
          open={isPdfEditorOpen}
          setOpen={setIsPdfEditorOpen}
          matterId={matterId}
          sourceFile={selectedFile}
          onSaved={() => {
            void onMatterFileSaved?.(selectedFile.id);
          }}
          showSaveAsCopyButton={false}
        />
      )}

      {selectedTemplate && isSelectedDocx && (
        <EditDocxTemplateModal
          open={isDocxEditorOpen}
          setOpen={setIsDocxEditorOpen}
          template={selectedTemplate}
          onSaved={() => {
            void onTemplateSaved?.(selectedTemplate.id, true);
          }}
          templateFieldScope={templateFieldScope}
        />
      )}

      {selectedFirmFile && isSelectedDocx && (
        <EditDocxFirmFileModal
          open={isDocxEditorOpen}
          setOpen={setIsDocxEditorOpen}
          sourceFile={selectedFirmFile}
          onSaved={() => {
            void onFirmFileSaved?.(selectedFirmFile.id);
          }}
          showSaveAsCopyButton={false}
          templateFieldScope={templateFieldScope}
        />
      )}

      {selectedFirmFile && isSelectedPdf && (
        <EditPdfFirmFileModal
          open={isPdfEditorOpen}
          setOpen={setIsPdfEditorOpen}
          sourceFile={selectedFirmFile}
          onSaved={() => {
            void onFirmFileSaved?.(selectedFirmFile.id);
          }}
        />
      )}
    </>
  );
};
